import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import GuideCapture from "@/components/GuideCapture";

const HERO_IMG = "/images/intensive-session.jpg";

const whyItems = [
  {
    n: "01",
    title: "Momentum without the wait",
    text: "Weekly therapy is powerful, but seven days between sessions can slow the work. An intensive keeps the thread in your hands from morning to afternoon.",
  },
  {
    n: "02",
    title: "Five uninterrupted hours",
    text: "No watching the clock. Enough room to move past the surface argument and into the pattern that keeps it going.",
  },
  {
    n: "03",
    title: "Structured, never overwhelming",
    text: "The day follows a clear arc — grounded in the Gottman Method and Emotionally Focused Therapy — with real breaks built in.",
  },
  {
    n: "04",
    title: "Private and unhurried",
    text: "Just the two of you and me. Nothing shared, nothing rushed, no one else in the schedule.",
  },
];

const fitItems = [
  "Rebuilding after infidelity or a serious breach of trust",
  "At a crossroads — deciding whether to stay or go",
  "Schedules that can't sustain weekly appointments",
  "Long-distance, or traveling in for care",
  "A strong relationship facing one big decision",
  "Preparing for marriage, a baby or a major move",
];

const steps = [
  {
    n: "01",
    title: "Before we meet",
    text: "Both partners complete a focused intake ahead of time, so not a minute of the day is spent on paperwork.",
  },
  {
    n: "02",
    title: "Morning — the full story",
    text: "We slow things down and map what keeps happening between you. Each partner is fully heard.",
  },
  {
    n: "03",
    title: "Midday — room to breathe",
    text: "A real lunch break. Rest, walk, let the morning settle before the deeper afternoon work.",
  },
  {
    n: "04",
    title: "Afternoon — the deeper work",
    text: "We work directly with the pattern — practicing repair, new conversations, and tools that hold up at home.",
  },
  {
    n: "05",
    title: "Before you leave — a plan",
    text: "You leave with a clear, written path forward and next steps we agree on together.",
  },
];

const CouplesIntensive = () => {
  usePageMeta(
    "Couples Therapy Intensive — One-Day Deep Dive | Holistic Healing Psychotherapy, Inc.",
    "A private 5-hour couples therapy intensive in California. Compress weeks of work into one focused day. $1,500–$2,000 with a California LCSW. Free 20-minute consultation."
  );

  return (
    <div data-testid="couples-intensive-page">
      <section className="bg-cream-50 pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeIn>
              <p className="eyebrow mb-6" data-testid="intensive-eyebrow">Couples Therapy Intensive · Premium Offering</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink" data-testid="intensive-headline">
                One focused day, devoted entirely to{" "}
                <em className="text-mauve-500">your relationship</em>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-xl" data-testid="intensive-subcopy">
                A private, structured deep-dive that compresses weeks of couples therapy into five focused hours —
                for couples who need meaningful movement now.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-5">
                <Link
                  to="/contact"
                  data-testid="intensive-schedule-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm tracking-wide px-7 py-4 transition-all duration-300 hover:bg-forest-700 hover:shadow-[0_12px_32px_rgba(18,55,42,0.3)]"
                >
                  Schedule a Consultation <ArrowRight size={16} />
                </Link>
                <Link
                  to="/couples-therapy"
                  data-testid="intensive-weekly-link"
                  className="text-sm text-forest-800 underline underline-offset-4 decoration-gold-500/60 hover:text-gold-700 transition-colors"
                >
                  Prefer weekly sessions?
                </Link>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.15} className="lg:col-span-5 editorial-frame">
            <img
              src={HERO_IMG}
              alt="A couple seated together on the couch, mid-conversation in a warm therapy room"
              className="relative z-10 w-full h-[340px] sm:h-[460px] object-cover"
              data-testid="intensive-hero-image"
            />
          </FadeIn>
        </div>
      </section>

      <section className="bg-forest-900 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading dark eyebrow="Why an Intensive" title="Sometimes a week between sessions is too long." />
          <div className="mt-14 grid md:grid-cols-2 gap-x-16">
            {whyItems.map((w, i) => (
              <FadeIn key={w.n} delay={i * 0.08}>
                <div className="group py-8 border-t border-cream-50/10 hover:border-gold-500/40 transition-colors duration-500" data-testid={`intensive-why-${w.n}`}>
                  <div className="flex gap-6">
                    <span className="mt-3 w-3 h-3 rotate-45 bg-gold-500/70 group-hover:bg-gold-400 transition-colors duration-500 shrink-0" aria-hidden="true" />
                    <div>
                      <h3 className="font-serif text-2xl text-cream-50 mb-3">{w.title}</h3>
                      <p className="text-base text-cream-50/70 leading-relaxed">{w.text}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-100 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Who It's For" title="Built for couples who can't wait for change." />
          <FadeIn delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="intensive-fit-list">
              {fitItems.map((f, i) => (
                <span
                  key={f}
                  data-testid={`intensive-fit-${i + 1}`}
                  className="rounded-full border border-forest-800/15 bg-cream-50 px-4 py-2 text-sm text-forest-800 transition-colors duration-300 hover:border-gold-500/60 hover:text-gold-700"
                >
                  {f}
                </span>
              ))}
            </div>
            <p className="mt-8 text-sm text-sage/80 max-w-2xl leading-relaxed" data-testid="intensive-fit-note">
              An intensive isn't right for every couple. If there is an active crisis, or one partner is unsure
              about staying in the relationship at all, weekly work may be the kinder pace — the free consultation
              helps us decide together.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="How the Day Unfolds" title="Five hours with a clear arc — and room to breathe." />
          <div className="mt-12">
            {steps.map((s, i) => (
              <FadeIn key={s.n} delay={i * 0.06}>
                <div className="flex gap-6 py-7 border-t border-forest-800/10" data-testid={`intensive-step-${s.n}`}>
                  <span className="mt-2 w-2.5 h-2.5 rotate-45 bg-gold-500/70 shrink-0" aria-hidden="true" />
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl text-forest-800 mb-2">{s.title}</h3>
                    <p className="text-base text-ink/70 leading-relaxed max-w-2xl">{s.text}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-50 pb-20 lg:pb-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <div className="rounded-2xl bg-forest-900 p-8 sm:p-12" data-testid="intensive-investment-card">
              <p className="eyebrow mb-4">Investment</p>
              <p className="font-serif text-4xl sm:text-5xl text-cream-50" data-testid="intensive-fee">
                $1,500–$2,000 <span className="text-xl text-cream-50/60 font-sans">· 5 focused hours</span>
              </p>
              <ul className="mt-7 space-y-3 text-sm text-cream-50/75 leading-relaxed max-w-xl" data-testid="intensive-includes-list">
                <li className="flex gap-3"><span className="text-gold-400 shrink-0">—</span> A focused intake for both partners before the day</li>
                <li className="flex gap-3"><span className="text-gold-400 shrink-0">—</span> Five private hours together — virtually, or in-office on Saturdays (in-office rates may vary)</li>
                <li className="flex gap-3"><span className="text-gold-400 shrink-0">—</span> A written plan with the next steps we agree on together</li>
              </ul>
              <p className="mt-6 text-xs text-cream-50/50 leading-relaxed max-w-xl" data-testid="intensive-policy-note">
                A 48-hour cancellation policy applies (late-cancel fee $800) — with flexibility when the unexpected
                happens. HSA/FSA accepted; superbills available for private-pay clients.
              </p>
              <Link
                to="/contact"
                data-testid="intensive-investment-cta"
                className="inline-flex items-center gap-2 mt-8 rounded-full bg-cream-50 text-forest-900 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-white"
              >
                Schedule a Consultation <ArrowRight size={15} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 pb-20 lg:pb-28" data-testid="guide-capture-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <GuideCapture />
          </FadeIn>
        </div>
      </section>

      <CTASection
        title="One day can change the direction of a relationship."
        copy="A free 20-minute consultation helps us decide whether an intensive — or weekly work — fits where you are."
      />
    </div>
  );
};

export default CouplesIntensive;
